import type { CatalogEntry } from '../types';

/**
 * Public / Open API (feature `15`) error codes + catalog.
 *
 * Code shape is `FFAAEE` (zero-padded decimal): `FF=15` (api), `AA` = the
 * endpoint within the public API (`00` = cross-endpoint shared, thrown by the
 * `ApiKeyGuard` / `ScopesGuard` / `RateLimitGuard` stack that runs on every
 * `/v2` handler), `EE` = the specific condition. `API_CODES` is the namespaced
 * accessor referenced in business code (`ErrorCode.Api.Shared.InvalidKey`);
 * `API_CATALOG` maps each code to its `{ httpStatus, message }`.
 *
 * Endpoint → `AA` map:
 *   00 shared (guard stack, cross-endpoint)
 *   01 key management (internal JWT-guarded `/api-keys` CRUD)
 *   02 partner integration (internal JWT-guarded `/partner/integration` CRUD)
 *   03 partner access request (the intake form that precedes 02)
 */
export const API_CODES = {
  /** Guard-stack failures shared across every `/v2` endpoint (AA = 00). */
  Shared: {
    /** No `Authorization: Bearer` API key was presented. */
    MissingKey: '150001',
    /** The presented key doesn't resolve to an active (non-revoked) key. */
    InvalidKey: '150002',
    /** The key lacks the scope the route requires. */
    ScopeForbidden: '150003',
    /** The key is not authorized for the workspace in the request path. */
    WorkspaceForbidden: '150004',
    /** The key has exceeded its plan's rate limit for this bucket. */
    RateLimitExceeded: '150005',
    /** Too many failed auth attempts from this caller — temporary cooldown. */
    TooManyFailedAttempts: '150006',
    /**
     * The key is valid, but the organization's plan doesn't include API access
     * (the `apiAccess` feature flag — off on `free`). Existing keys stay listed
     * and revocable; they authenticate again after an upgrade.
     */
    ApiAccessNotInPlan: '150007',
  },
  /** Internal key-management CRUD failures (AA = 01). */
  KeyManagement: {
    /** A workspace (`ti_ws_`) key was requested without a `workspaceId`. */
    WorkspaceRequired: '150101',
    /** The creator may not mint a key that out-ranks their own workspace role. */
    RoleExceedsCreator: '150102',
    /** The creator is not a member of (or lacks rights on) the target workspace. */
    WorkspaceForbidden: '150103',
    /** No key with that id exists in the caller's organization. */
    KeyNotFound: '150104',
  },
  /**
   * Partner callback registration (AA = 02) — the `host` + `accessToken` pair a
   * partner registers so TrulyInbox can mint access tokens for the mailboxes
   * they connect. See docs/specs/2026-08-21-partner-warmup-integration.md §3.
   */
  PartnerIntegration: {
    /** `host` is not an absolute HTTPS origin, or carries a path/query/fragment. */
    InvalidHost: '150201',
    /** The callback bearer token is shorter than the required minimum. */
    TokenTooWeak: '150202',
    /**
     * The verification ping did not return `200 {"status":"ok"}`. Nothing was
     * persisted — the ping runs BEFORE the write, so a failure leaves no row.
     */
    VerificationFailed: '150203',
    /** The caller's organization has no partner integration registered. */
    NotRegistered: '150204',
    /**
     * Removal rolled back because a mailbox still referenced the integration
     * when the row was deleted (the FK's `ON DELETE RESTRICT`, errno 1451).
     * Removal normally DELETES every mailbox linked to the integration, under a
     * lock that serializes racing connects, so this is a backstop — nothing was
     * changed, and a retry is the remedy. The key name predates that: removal
     * used to be refused outright while any mailbox used it. Kept, not renamed —
     * clients branch on the code.
     */
    HasActiveAccounts: '150205',
    /**
     * The removal confirmation code is wrong, expired (10 minutes), already
     * used, or was burned after too many wrong attempts. One code for all four
     * on purpose — the remedy is the same (request a new code), and telling a
     * guesser which case they hit helps nobody else.
     */
    InvalidOrExpiredRemovalCode: '150206',
    /**
     * Removal codes were requested too often for this organization. Each
     * request emails EVERY owner, so it is capped (cooldown + hourly ceiling).
     */
    RemovalCodeRateLimited: '150207',
    /**
     * No owner could be emailed the removal code (SMTP failure, or every owner
     * address was refused). Nothing was stored, so no code is pending.
     */
    RemovalCodeNotDelivered: '150208',
  },
  /**
   * Partner access-request intake (AA = 03) — the three-field form an org owner
   * submits to ASK for partner access, which precedes the registration above.
   * Its public (unauthenticated) twin on the docs site writes a NULL-org row and
   * is deliberately not deduplicated, so it can raise none of these.
   */
  PartnerAccessRequest: {
    /**
     * The caller's organization already has a request on file. Enforced by the
     * UNIQUE index on `partnerIntegrationForm.organizationId`, so a concurrent
     * double-submit raises this too rather than inserting a second row.
     */
    AlreadySubmitted: '150301',
    /** Notify called for an org with no request on file. The web card only calls it
     * after a successful submit, so this surfaces a client bug rather than a 204. */
    NotFound: '150302',
    /** `POST .../access-request/calendly-booked`: the picked slot is no longer in the future.
     * Mirrors `Onboarding.CalendlyBooked.SlotInThePast`, but owned by this endpoint. */
    SlotInThePast: '150303',
    /** `CALENDLY_PARTNER_SCHEDULING_URL` (or the shared token) is unset. */
    NotConfigured: '150304',
    /** Calendly's `/invitees` call failed or returned an unusable response. */
    CalendlyRequestFailed: '150305',
  },
} as const;

export const API_CATALOG: Record<string, CatalogEntry> = {
  // ── Shared guard stack (AA = 00) ───────────────────────────────────────────
  '150001': {
    httpStatus: 401,
    message: 'API key is required.',
  },
  '150002': {
    httpStatus: 401,
    message: 'Invalid or revoked API key.',
  },
  '150003': {
    httpStatus: 403,
    message: "Your API key doesn't have the required scope for this action.",
  },
  '150004': {
    httpStatus: 403,
    message: "Your API key doesn't have access to this workspace.",
  },
  '150005': {
    httpStatus: 429,
    message: 'API rate limit exceeded. Try again shortly.',
  },
  '150006': {
    httpStatus: 429,
    message: 'Too many failed authentication attempts. Try again later.',
  },
  '150007': {
    httpStatus: 403,
    message: "API access isn't included in your plan. Upgrade to use the API.",
  },

  // ── Key management (AA = 01) ────────────────────────────────────────────────
  '150101': {
    httpStatus: 400,
    message: 'A workspace id is required to create a workspace API key.',
  },
  '150102': {
    httpStatus: 403,
    message: "You can't create a key with a role higher than your own.",
  },
  '150103': {
    httpStatus: 403,
    message: "You don't have access to the workspace for this key.",
  },
  '150104': {
    httpStatus: 404,
    message: 'API key not found.',
  },

  // ── Partner integration (AA = 02) ───────────────────────────────────────────
  '150201': {
    httpStatus: 400,
    message: 'Callback host must be an HTTPS URL with no path, query, or fragment.',
  },
  '150202': {
    httpStatus: 400,
    message: 'Callback token must be at least 32 characters.',
  },
  '150203': {
    httpStatus: 400,
    message:
      'We could not verify your callback endpoint. Make sure it is live and returns 200 with {"status":"ok"}, then try again.',
  },
  '150204': {
    httpStatus: 404,
    message: 'No partner integration is registered for this organization.',
  },
  '150205': {
    httpStatus: 409,
    message:
      'A mailbox was connected to this integration while it was being removed. Nothing was changed. Try again.',
  },
  '150206': {
    httpStatus: 400,
    message: 'That confirmation code is invalid or has expired. Request a new code and try again.',
  },
  '150207': {
    httpStatus: 429,
    message: 'A confirmation code was requested too recently. Wait a moment before requesting another.',
  },
  '150208': {
    httpStatus: 503,
    message: "We couldn't email the confirmation code to your organization's owners. Please try again.",
  },

  // ── Partner access request (AA = 03) ────────────────────────────────────────
  '150301': {
    httpStatus: 409,
    message: "You've already requested partner access. We'll get back to you.",
  },
  '150302': {
    httpStatus: 404,
    message: "We couldn't find a partner-access request for your organization.",
  },
  '150303': {
    httpStatus: 400,
    message: 'That time has already passed - please pick another slot.',
  },
  '150304': {
    httpStatus: 503,
    message: "Booking isn't available right now. Please try again later.",
  },
  '150305': {
    httpStatus: 502,
    message: "We couldn't reach our scheduling provider. Please try again.",
  },
};
